import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { environment } from "environments/environment";
import { User } from "@type/User";
import { ApiResponse } from "@type/ApiResponse";
import { ApiClass } from "@class/ApiClass";

export type UserAndToken = {
  token: string;
  user: User;
};

export type LoginResponse = ApiResponse<UserAndToken>;

@Injectable({ providedIn: "root" })
export class AuthenticationService extends ApiClass {
  //public
  public currentUser: Observable<UserAndToken>;

  //private
  private currentUserSubject: BehaviorSubject<UserAndToken>;

  constructor() {
    super();
    this.currentUserSubject = new BehaviorSubject<UserAndToken>(null);
    this.currentUser = this.currentUserSubject.asObservable();
  }

  // getter: currentUserValue
  public get currentUserValue() {
    return this.currentUserSubject.value?.user;
  }

  public get currentToken() {
    return this.currentUserSubject.value?.token;
  }

  /**
   *  Confirms if user is admin
   */
  get isAdmin() {
    return this.currentUser && this.currentUserSubject.value.user.role.key === "ADMIN";
  }

  /**
   *  Confirms if user is client
   */
  get isClient() {
    return this.currentUser && this.currentUserSubject.value.user.role.key === "CLIENTE";
  }

  /**
   * User login
   *
   * @param email
   * @param password
   * @returns user
   */
  login(email: string, password: string) {
    return this._http.post<LoginResponse>(`${environment.apiUrl}/login`, { email, password }).pipe(
      map(r => {
        if (r.ok === false) {
          throw r.errors;
        }
        const { data } = r;
        localStorage.setItem("userToken", data.token);

        this.currentUserSubject.next(data);

        return r;
      })
    );
  }

  /**
   * User sign in with Google
   *
   * @param email
   * @param password
   * @returns user
   */
  googleSignIn(credential: string) {
    return this._http
      .post<LoginResponse>(`${environment.apiUrl}/google-sign-in`, {
        credential
      })
      .pipe(
        map(r => {
          if (r.ok === false) {
            throw r.errors;
          }
          const { data } = r;
          localStorage.setItem("userToken", data.token);
          this.currentUserSubject.next(data);
          return r;
        })
      );
  }

  recoveryPassword(email: string) {
    return this.post("/forgot-password", { email });
  }

  createUser(email: string) {
    return this.post("/users", { email });
  }

  /**
   * User logout
   *
   */
  logout() {
    this._http.post(`${environment.apiUrl}/logout`, {}).subscribe();
    // remove user from local storage to log user out
    localStorage.removeItem("userToken");
    // notify
    this.currentUserSubject.next(null);
  }

  /**
   * Revokes Google app concent permissions
   * @returns
   */
  revokeGoogleConsent(email: string) {
    // @ts-ignore
    google.accounts.id.revoke(email);
  }

  verifyToken() {
    const token = localStorage.getItem("userToken");
    if (!token) {
      return of(null);
    }
    return this._http.get<ApiResponse<User>>(`${environment.apiUrl}/user`).pipe(
      catchError(err => {
        localStorage.removeItem("userToken");
        this.currentUserSubject.next(null);
        throw err;
      }),
      map(r => {
        if (r.ok === false) {
          localStorage.removeItem("userToken");
          throw r.errors;
        }
        const currentToken = this.currentUserSubject.value?.token || localStorage.getItem("userToken");
        const { data } = r;
        const tokenAndUser = { token: currentToken, user: data };
        this.currentUserSubject.next(tokenAndUser);

        return r;
      })
    );
  }
}
